import { useAuth0 } from '@auth0/auth0-react'

import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { removeTrailingSlash } from '../utils'
import { RegisterButton } from './RegisterButton'

export const Footer: React.FC = () => {
  const { isAuthenticated } = useAuth0()
  const thisPage = removeTrailingSlash(useLocation().pathname)
  const agencyDetailPage = thisPage.match(/agencies\/\d*/)
  const hospitalDetailPage = thisPage.match(/hospitals\/\d*/)
  const [showScrollTop, setShowScrollTop] = useState(false)

  // Show scroll to top button when user scrolls down
  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ behavior: 'smooth', top: 0 })
  }

  return (
    <div className="relative">
      {/* CTA Banner Section — only shown to guests, not to logged-in users */}
      {!isAuthenticated && !agencyDetailPage && !hospitalDetailPage && (
        <div className="px-4 md:px-8 mb-4 md:mb-8">
          <div
            className="py-6 md:py-12 px-4 md:px-12 rounded-2xl max-w-6xl mx-auto"
            style={{
              background: 'linear-gradient(to right, #308b97, #3aa8b5)',
            }}
          >
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-bold text-2xl md:text-4xl text-white mb-2 md:mb-4">Ready to Start Your Journey?</h2>
              <p className="text-base md:text-xl text-white mb-4 md:mb-8">
                Join thousands of healthcare workers finding their perfect assignments
              </p>
              <RegisterButton className="bg-orange text-white px-6 md:px-8 py-2 md:py-3 text-base md:text-lg font-semibold rounded-md inline-block hover:bg-opacity-90 transition-all">
                Get Started Today
              </RegisterButton>
            </div>
          </div>
        </div>
      )}

      {/* Main Footer */}
      <div className="bg-gray-800 pb-safe-bottom text-white">
        <div className="max-w-6xl mx-auto px-4 py-6 md:py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 mb-4 md:mb-8">
            {/* Brand Column */}
            <div>
              <h3 className="text-aqua-mint text-xl md:text-2xl font-bold mb-2 md:mb-4">ScrubStr</h3>
              <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
                The trusted platform for healthcare workers to find and review hospitals and agencies.
              </p>
            </div>

            {/* For Nurses Column */}
            <div>
              <h4 className="text-white font-semibold text-base md:text-lg mb-2 md:mb-4">For Nurses</h4>
              <ul className="space-y-1 md:space-y-2">
                <li>
                  <Link
                    to="/hospitals#tab=&state=&city="
                    className="text-gray-300 hover:text-white transition-colors text-sm md:text-base"
                  >
                    Find Hospitals
                  </Link>
                </li>
                <li>
                  <Link
                    to="/hospitals#state=&city=&tab=agencies"
                    className="text-gray-300 hover:text-white transition-colors text-sm md:text-base"
                  >
                    Find Agencies
                  </Link>
                </li>
                <li>
                  <Link
                    to="/hospitals"
                    className="text-gray-300 hover:text-white transition-colors text-sm md:text-base"
                  >
                    Write Reviews
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h4 className="text-white font-semibold text-base md:text-lg mb-2 md:mb-4">Company</h4>
              <ul className="space-y-1 md:space-y-2">
                <li>
                  <Link to="/terms" className="text-gray-300 hover:text-white transition-colors text-sm md:text-base">
                    Terms of Use
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-300 hover:text-white transition-colors text-sm md:text-base">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright Line */}
          <div className="border-t border-gray-700 pt-4 md:pt-6">
            <p className="text-center text-gray-400 text-xs md:text-sm">
              © {new Date().getFullYear()} ScrubStr. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-gray-600 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 transition-all z-50"
          aria-label="Scroll to top"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  )
}
